export const ADMIN_ONBOARDING_BYPASS_STORAGE_PREFIX =
  "tfg.supplierOnboarding.adminBypass." as const

export const ADMIN_ONBOARDING_BYPASS_FLAG_VALUE = "1" as const

export type AdminOnboardingBypassScope = Readonly<{
  supplierId: string
  userId: string
}>

export const buildAdminOnboardingBypassStorageKey = (
  scope: AdminOnboardingBypassScope
): string =>
  `${ADMIN_ONBOARDING_BYPASS_STORAGE_PREFIX}${scope.supplierId}-${scope.userId}`

const getSessionStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null
  }
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

type AdminOnboardingBypassListener = () => void

const adminOnboardingBypassListeners = new Set<AdminOnboardingBypassListener>()
let adminOnboardingBypassStoreVersion = 0

const isValidScope = (scope: AdminOnboardingBypassScope): boolean =>
  scope.supplierId.length > 0 && scope.userId.length > 0

const notifyAdminOnboardingBypassListeners = (): void => {
  adminOnboardingBypassStoreVersion += 1
  for (const listener of adminOnboardingBypassListeners) {
    listener()
  }
}

export const getAdminOnboardingBypassStoreVersion = (): number =>
  adminOnboardingBypassStoreVersion

/** Lets the mounted onboarding gate re-read sessionStorage after Skip. */
export const subscribeAdminOnboardingBypass = (
  onStoreChange: AdminOnboardingBypassListener
): (() => void) => {
  adminOnboardingBypassListeners.add(onStoreChange)
  return () => {
    adminOnboardingBypassListeners.delete(onStoreChange)
  }
}

export const hasAdminOnboardingBypass = (
  scope: AdminOnboardingBypassScope
): boolean => {
  const storage = getSessionStorage()
  if (!storage || !isValidScope(scope)) {
    return false
  }
  try {
    return (
      storage.getItem(buildAdminOnboardingBypassStorageKey(scope)) ===
      ADMIN_ONBOARDING_BYPASS_FLAG_VALUE
    )
  } catch {
    return false
  }
}

/** Persists the session bypass flag. Returns true only when the flag is readable after write. */
export const setAdminOnboardingBypass = (
  scope: AdminOnboardingBypassScope
): boolean => {
  const storage = getSessionStorage()
  if (!storage || !isValidScope(scope)) {
    return false
  }
  try {
    const storageKey = buildAdminOnboardingBypassStorageKey(scope)
    storage.setItem(storageKey, ADMIN_ONBOARDING_BYPASS_FLAG_VALUE)
    const didPersist =
      storage.getItem(storageKey) === ADMIN_ONBOARDING_BYPASS_FLAG_VALUE
    if (!didPersist) {
      return false
    }
    notifyAdminOnboardingBypassListeners()
    return true
  } catch {
    return false
  }
}

export const clearAdminOnboardingBypass = (
  scope: AdminOnboardingBypassScope
): void => {
  const storage = getSessionStorage()
  if (!storage || !isValidScope(scope)) {
    return
  }
  try {
    storage.removeItem(buildAdminOnboardingBypassStorageKey(scope))
    notifyAdminOnboardingBypassListeners()
  } catch {
    // ignore
  }
}

/** Clears every admin onboarding bypass key in sessionStorage (e.g. on logout). */
export const clearAllAdminOnboardingBypasses = (): void => {
  const storage = getSessionStorage()
  if (!storage) {
    return
  }
  try {
    const keysToRemove: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith(ADMIN_ONBOARDING_BYPASS_STORAGE_PREFIX) === true) {
        keysToRemove.push(key)
      }
    }
    for (const key of keysToRemove) {
      storage.removeItem(key)
    }
    notifyAdminOnboardingBypassListeners()
  } catch {
    // ignore
  }
}
